/**
 * Cancel a dispatch, with a reason (batch 8 T12c1, #3033).
 *
 * A queued dispatch is removed from the queue. A request that may already have left stays as it is: the cancellation is recorded in
 * OS Buddy only, the supplier is NOT told, and the dispatch still needs reconciliation. The dialog says so before the user confirms,
 * and the answer's `requestMayHaveLeft` keeps the warning on screen afterwards. After an unknown outcome the dialog closes, the state is
 * refreshed and the unknown outcome stays visible on the dispatch until acknowledged; the cancel is never repeated on its own.
 */

import { useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import { useCancelDispatch } from '@/hooks/useProcurementDispatch';
import { describeDispatchError } from './dispatchErrors';
import type { DispatchCancelResult, DispatchSummary } from './dispatchTypes';

const CANCEL_REASON_MIN = 3;
const CANCEL_REASON_MAX = 500;

interface CancelDispatchDialogProps {
  open: boolean;
  onClose: () => void;
  summary: DispatchSummary;
  onDone?: ((result: DispatchCancelResult) => void) | undefined;
}

export function CancelDispatchDialog({
  open,
  onClose,
  summary,
  onDone,
}: CancelDispatchDialogProps) {
  const { t } = useTranslation('procurementDispatch');
  const { t: tc } = useTranslation('common');
  const command = useCancelDispatch();
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [blocked, setBlocked] = useState(false);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < CANCEL_REASON_MIN;
  const tooLong = trimmed.length > CANCEL_REASON_MAX;
  const invalid = tooShort || tooLong;

  const submit = async () => {
    setTouched(true);
    if (invalid) return;
    setError(null);
    setBlocked(false);
    const result = await command.run({
      purchaseOrderId: summary.purchaseOrderId,
      dispatchId: summary.id,
      reason: trimmed,
    });
    if (result.status === 'success') {
      onDone?.(result.result);
      onClose();
    } else if (result.status === 'unknown') {
      onClose();
    } else if (result.status === 'refused') {
      setError(result.error);
    } else if (result.status === 'blocked') {
      setBlocked(true);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={command.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      mobileTitle={t('cancel.title')}
      aria-label={t('cancel.title')}
    >
      <DialogHeader title={t('cancel.title')} onClose={command.isPending ? undefined : onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {summary.requestMayHaveLeft ? (
            <Alert severity="warning" role="alert">
              <Typography variant="body2" fontWeight={600}>
                {t('cancel.mayHaveLeftTitle')}
              </Typography>
              <Typography variant="body2">{t('cancel.mayHaveLeftBody')}</Typography>
              <Typography variant="body2">{t('cancel.notPerformed')}</Typography>
            </Alert>
          ) : (
            <Typography variant="body2">{t('cancel.queuedBody')}</Typography>
          )}
          <TextField
            label={t('cancel.reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
            required
            multiline
            minRows={2}
            disabled={command.isPending}
            error={touched && invalid}
            helperText={
              touched && tooShort
                ? t('validation.reasonTooShort', { min: CANCEL_REASON_MIN })
                : touched && tooLong
                  ? t('validation.reasonTooLong', { max: CANCEL_REASON_MAX })
                  : t('cancel.reasonHint')
            }
            slotProps={{ htmlInput: { maxLength: CANCEL_REASON_MAX * 2 } }}
          />
          {blocked && (
            <Alert severity="warning" role="alert">
              {t('unknown.blockedRepeat')}
            </Alert>
          )}
          {error !== null && (
            <Alert severity="error" role="alert">
              {describeDispatchError(t, error)}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={command.isPending}>
          {tc('close')}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => void submit()}
          disabled={command.isPending}
        >
          {command.isPending ? t('cancel.cancelling') : t('cancel.confirm')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
